import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { Plus, Search, Download, Upload, Edit2, Trash2, MessageSquare, ChevronUp, ChevronDown, ChevronsUpDown, Columns, Check } from "lucide-react";
import WaitlistEntryModal from "../components/WaitlistEntryModal";
import { useAuth } from "../contexts/AuthContext";

const API = window.location.origin + "/api";

// Définition de toutes les colonnes disponibles
const ALL_COLUMNS = [
  { key: "priorite",            label: "Prio.",                    sortField: "priorite",             defaultVisible: true },
  { key: "couleur",             label: "Statut",                   sortField: "couleur",              defaultVisible: true },
  { key: "dispositif",          label: "Dispositif",               sortField: "dispositif",           defaultVisible: true },
  { key: "nom_prenom",          label: "Nom / Prénom",             sortField: "nom",                  defaultVisible: true },
  { key: "date_naissance",      label: "Date de naissance",        sortField: "date_naissance",       defaultVisible: true },
  { key: "composition",         label: "Composition",              sortField: "composition_familiale", defaultVisible: true },
  { key: "nb_enfants",          label: "Nombre d'enfants",         sortField: "nb_enfants",           defaultVisible: true },
  { key: "degre_accompagnement",label: "Intensité accompagnement", sortField: "degre_accompagnement", defaultVisible: true },
  { key: "sante",               label: "Problématique santé",      sortField: "sante",                defaultVisible: true },
  { key: "date_demande",        label: "Demande",                  sortField: "date_demande",         defaultVisible: true },
  { key: "date_commission",     label: "Commission",               sortField: "date_commission",      defaultVisible: true },
  { key: "secteur",             label: "Secteur",                  sortField: "secteur_geographique", defaultVisible: true },
  { key: "referent",            label: "Référent",                 sortField: "referent",             defaultVisible: true },
  { key: "ressource",           label: "Ressource",                sortField: "ressource",            defaultVisible: true },
  { key: "commentaire",         label: "Commentaire",              sortField: null,                   defaultVisible: true },
  { key: "date_commentaire",    label: "Dt commentaire",           sortField: "date_commentaire",     defaultVisible: true },
];

const DISPOSITIFS = [
  "", "ALTER LOGEMENTS", "AVDL", "BAIL ACCOMPAGNE ASL", "CHRS CO LOCATION",
  "CHRS COLLECTIF ASILE DE NUIT", "CHRS COLLECTIF FEMME", "CHRS COLLECTIF RENAITRE",
  "CHRS COLLECTIF ROANNE", "CHRS DIFFUS", "CHRS HORS LES MURS",
  "DISPOSITIF SPECIFIQUE DEDIE AUX SORTANTS DU CD DE ROANNE",
  "DROITS INCOMPLETS", "FJT CLAIRVIVRE", "FJT ROANNE", "ILOT JEUNES",
  "IML FVV", "IML MANDAT DE GESTION", "IML SOUS LOCATION", "J'APPART",
  "LOGEMENTS PASSERELLE", "PENSION DE FAMILLE", "RESIDENCE ACCUEIL BAUDELAIRE",
  "RESIDENCE SOCIALE", "RESIDENCE SOCIALE JEUNES ASL",
];

const COULEUR_CFG = {
  blanc: {
    label: "Sur liste",
    rowCls: "bg-slate-100 border-l-4 border-slate-400 hover:bg-slate-200",
    badgeCls: "bg-slate-200 text-slate-800 border border-slate-400",
    dotCls: "bg-slate-500",
  },
  bleu: {
    label: "En cours",
    rowCls: "bg-blue-50 border-l-4 border-blue-500 hover:bg-blue-100",
    badgeCls: "bg-blue-100 text-blue-800 border border-blue-300",
    dotCls: "bg-blue-500",
  },
  rouge: {
    label: "À supprimer",
    rowCls: "bg-red-50 border-l-4 border-red-500 hover:bg-red-100",
    badgeCls: "bg-red-100 text-red-800 border border-red-300",
    dotCls: "bg-red-500",
  },
  gris: {
    label: "À revoir",
    rowCls: "bg-orange-50 border-l-4 border-orange-400 hover:bg-orange-100",
    badgeCls: "bg-orange-100 text-orange-800 border border-orange-300",
    dotCls: "bg-orange-400",
  },
};

const USER_COLOR_HEX = {
  violet: "#8B5CF6", vert: "#10B981", orange: "#F97316", rose: "#EC4899",
  cyan: "#06B6D4", rouge: "#EF4444", bleu: "#3B82F6", jaune: "#EAB308",
  indigo: "#6366F1", teal: "#14B8A6", marron: "#92400E", gris: "#6B7280",
};

const PAGE_SIZE = 30;

function ReferentBadge({ name, colorMap }) {
  if (!name) return <span className="text-slate-400 text-xs">—</span>;
  const hex = colorMap[name.toLowerCase()];
  if (!hex) return <span className="text-slate-600 text-xs">{name}</span>;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ backgroundColor: hex + "18", color: hex, border: `1px solid ${hex}44` }}>
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: hex }} />
      {name}
    </span>
  );
}

function SortHeader({ field, label, sortField, sortDir, onSort }) {
  const active = sortField === field;
  return (
    <th
      className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 cursor-pointer select-none hover:text-indigo-600 hover:bg-indigo-50 transition-colors bg-slate-100"
      onClick={() => onSort(field)}
      data-testid={`sort-${field}`}
    >
      <span className="flex items-center gap-1">
        {label}
        {active ? (
          sortDir === "asc"
            ? <ChevronUp className="w-3.5 h-3.5 text-indigo-500" />
            : <ChevronDown className="w-3.5 h-3.5 text-indigo-500" />
        ) : (
          <ChevronsUpDown className="w-3.5 h-3.5 text-slate-300" />
        )}
      </span>
    </th>
  );
}

export default function WaitlistPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterDispositif, setFilterDispositif] = useState("");
  const [filterCouleur, setFilterCouleur] = useState("");
  const [filterReferent, setFilterReferent] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState({ status: "", imported: 0, total: 0 });
  const [importResult, setImportResult] = useState(null); // { type: 'success'|'error', message, details }
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [changingColor, setChangingColor] = useState(null);
  const [expandedComment, setExpandedComment] = useState(null);
  const [sortField, setSortField] = useState("date_demande");
  const [sortDir, setSortDir] = useState("asc");
  const [visibleCols, setVisibleCols] = useState(
    () => new Set(ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key))
  );
  const [showColPicker, setShowColPicker] = useState(false);
  const colPickerRef = useRef(null);

  // Charger les préférences depuis SharePoint au montage
  useEffect(() => {
    axios.get(`${API}/users/me/preferences`)
      .then(res => {
        if (res.data?.visibleCols?.length) {
          // Fusionner avec les nouvelles colonnes (defaultVisible: true) non encore dans les prefs
          const saved = new Set(res.data.visibleCols);
          ALL_COLUMNS.forEach(col => {
            if (col.defaultVisible && !saved.has(col.key)) {
              saved.add(col.key);
            }
          });
          setVisibleCols(saved);
        }
      })
      .catch(() => {});
  }, []);

  // Sauvegarder dans SharePoint quand les colonnes changent (debounce 1s)
  useEffect(() => {
    const timer = setTimeout(() => {
      axios.put(`${API}/users/me/preferences`, {
        visibleCols: Array.from(visibleCols)
      }).catch(() => {});
    }, 1000);
    return () => clearTimeout(timer);
  }, [visibleCols]);

  // Fermer le picker en cliquant ailleurs
  useEffect(() => {
    const handler = (e) => { if (colPickerRef.current && !colPickerRef.current.contains(e.target)) setShowColPicker(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleCol = (key) => {
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 1) next.delete(key); }
      else next.add(key);
      return next;
    });
  };

  const activeCols = ALL_COLUMNS.filter(c => visibleCols.has(c.key)); // "asc" | "desc"

  // Load referent colors (accessible à tous les utilisateurs connectés)
  useEffect(() => {
    axios.get(`${API}/users/referents`).then(res => setUsers(res.data)).catch(() => {});
  }, []);

  // Build a map: name (lowercase) → hex color
  const referentColorMap = useMemo(() => {
    const map = {};
    users.forEach(u => {
      if (u.name && u.couleur) {
        map[u.name.toLowerCase()] = USER_COLOR_HEX[u.couleur] || "#6366F1";
      }
    });
    return map;
  }, [users]);

  // Unique referent names from entries for the filter dropdown
  const referentList = useMemo(() => {
    const names = [...new Set(entries.map(e => e.referent).filter(Boolean))];
    return names.sort();
  }, [entries]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterDispositif) params.dispositif = filterDispositif;
      if (filterCouleur) params.couleur = filterCouleur;
      if (filterReferent) params.referent = filterReferent;
      if (search) params.search = search;
      const { data } = await axios.get(`${API}/waitlist`, { params });
      setEntries(data);
      setPage(1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterDispositif, filterCouleur, filterReferent, search]);

  useEffect(() => {
    const timer = setTimeout(fetchEntries, 300);
    return () => clearTimeout(timer);
  }, [fetchEntries]);

  const handleSave = async (form) => {
    try {
      if (editEntry) {
        await axios.put(`${API}/waitlist/${editEntry.id}`, form);
      } else {
        await axios.post(`${API}/waitlist`, form);
      }
      setModalOpen(false);
      setEditEntry(null);
      fetchEntries();
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/waitlist/${id}`);
      setDeleteId(null);
      fetchEntries();
    } catch (err) {
      alert("Erreur lors de la suppression");
    }
  };

  const handleColorChange = async (id, couleur) => {
    try {
      await axios.patch(`${API}/waitlist/${id}/couleur`, { couleur });
      setEntries(prev => prev.map(e => e.id === id ? { ...e, couleur } : e));
    } catch (err) {
      console.error(err);
    }
    setChangingColor(null);
  };

  const handleReset = async () => {
    setResetLoading(true);
    try {
      const { data } = await axios.delete(`${API}/waitlist/all`, { timeout: 60000 });
      setResetConfirm(false);
      setImportResult({ type: "success", message: data.message, details: null });
      fetchEntries();
    } catch (err) {
      setImportResult({ type: "error", message: "Erreur lors de la suppression", details: err.response?.data?.detail || err.message });
      setResetConfirm(false);
    } finally {
      setResetLoading(false);
    }
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    if (filterDispositif) params.append("dispositif", filterDispositif);
    if (filterCouleur) params.append("couleur", filterCouleur);
    window.open(`${API}/export/excel?${params}`, "_blank");
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const sizeMB = file.size / 1024 / 1024;
    if (sizeMB > 20) {
      setImportResult({ type: "error", message: `Fichier trop volumineux (${sizeMB.toFixed(1)} Mo).`, details: null });
      e.target.value = "";
      return;
    }
    setImportLoading(true);
    setImportResult(null);
    setImportProgress({ status: "parsing", imported: 0, total: 0 });
    try {
      // 1. Envoyer le fichier — retour immédiat avec job_id
      const formData = new FormData();
      formData.append("file", file);
      const { data: startData } = await axios.post(`${API}/import/excel`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });
      const jobId = startData.job_id;

      // 2. Polling de la progression toutes les 1.5s
      await new Promise((resolve, reject) => {
        const poll = setInterval(async () => {
          try {
            const { data: progress } = await axios.get(`${API}/import/progress/${jobId}`);
            setImportProgress(progress);
            if (progress.status === "done") {
              clearInterval(poll);
              setImportResult({
                type: "success",
                message: `${progress.imported} entrées importées dans SharePoint`,
                details: null,
              });
              fetchEntries();
              resolve();
            } else if (progress.status === "error") {
              clearInterval(poll);
              setImportResult({ type: "error", message: "Erreur lors de l'import", details: progress.error });
              reject(new Error(progress.error));
            }
          } catch (err) {
            clearInterval(poll);
            reject(err);
          }
        }, 1500);
      });
    } catch (err) {
      const status = err.response?.status;
      setImportResult({
        type: "error",
        message: status === 403 ? "Accès refusé"
          : status === 413 ? "Fichier trop volumineux"
          : `Erreur (${status || "réseau"})`,
        details: err.response?.data?.detail || err.message,
      });
    } finally {
      setImportLoading(false);
      setImportProgress({ status: "", imported: 0, total: 0 });
      e.target.value = "";
    }
  };

  // Parse date "JJ/MM/AAAA" → sortable number (AAAAMMJJ)
  const parseDate = (s) => {
    if (!s) return 0;
    const parts = s.split("/");
    if (parts.length === 3) return parseInt(parts[2] + parts[1] + parts[0], 10) || 0;
    return 0;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
    setPage(1);
  };

  const sortedEntries = useMemo(() => {
    let arr = [...entries];

    // Filtre date demande (client-side, format input YYYY-MM-DD → nombre YYYYMMDD)
    const toNum = (isoDate) => isoDate ? parseInt(isoDate.replace(/-/g, ""), 10) : 0;
    const fromNum = toNum(filterDateFrom);
    const toNumVal = toNum(filterDateTo);
    if (fromNum || toNumVal) {
      arr = arr.filter(e => {
        const d = parseDate(e.date_demande);
        if (!d) return false;
        if (fromNum && d < fromNum) return false;
        if (toNumVal && d > toNumVal) return false;
        return true;
      });
    }

    arr.sort((a, b) => {
      let va, vb;
      if (sortField === "date_demande" || sortField === "date_commission" || sortField === "date_orientation" || sortField === "date_commentaire" || sortField === "date_naissance") {
        va = parseDate(a[sortField]);
        vb = parseDate(b[sortField]);
      } else if (sortField === "priorite") {
        va = a.priorite ?? 9999;
        vb = b.priorite ?? 9999;
      } else {
        va = (a[sortField] || "").toLowerCase();
        vb = (b[sortField] || "").toLowerCase();
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [entries, sortField, sortDir, filterDateFrom, filterDateTo]);

  // Avec scroll interne, afficher toutes les entrées sans pagination
  const paginated = sortedEntries;

  return (
    <div data-testid="waitlist-page" style={{ fontFamily: "Manrope, sans-serif" }}>

      {/* ── EN-TÊTE FIGÉ ── */}
      <div className="sticky top-0 z-20 bg-slate-50 pb-4 -mx-6 px-6 lg:-mx-8 lg:px-8 pt-2 border-b border-slate-200 shadow-sm mb-4">

        {/* Titre + boutons */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Liste d'attente
            </h1>
            <p className="text-slate-500 text-sm mt-0.5">{sortedEntries.length} entrée{sortedEntries.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <label className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer transition-colors"
                data-testid="import-btn">
                <Upload className="w-4 h-4" />
                {importLoading ? "Import..." : "Importer"}
                <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImport} />
              </label>
            )}
            {isAdmin && (
              <button onClick={handleExport} data-testid="export-btn"
                className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                <Download className="w-4 h-4" />
                Exporter
              </button>
            )}
            {isAdmin && (
              <button onClick={() => setResetConfirm(true)} data-testid="reset-btn"
                className="flex items-center gap-1.5 px-3 py-2 bg-white border border-red-200 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors">
                <Trash2 className="w-4 h-4" />
                Vider la liste
              </button>
            )}
            {/* Sélecteur de colonnes */}
            <div className="relative" ref={colPickerRef}>
              <button onClick={() => setShowColPicker(v => !v)}
                className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                <Columns className="w-4 h-4" />
                Colonnes
              </button>
              {showColPicker && (
                <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-slate-200 rounded-xl shadow-xl p-3 w-52">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 px-1">Colonnes visibles</p>
                  {ALL_COLUMNS.map(col => (
                    <button key={col.key} onClick={() => toggleCol(col.key)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 text-sm text-slate-700 text-left">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${visibleCols.has(col.key) ? "bg-indigo-600 border-indigo-600" : "border-slate-300"}`}>
                        {visibleCols.has(col.key) && <Check className="w-3 h-3 text-white" />}
                      </div>
                      {col.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => { setEditEntry(null); setModalOpen(true); }}
              data-testid="add-entry-btn"
              className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
              <Plus className="w-4 h-4" />
              Nouvelle entrée
            </button>
          </div>
        </div>

        {/* Légende + Filtres sur la même ligne */}
        <div className="flex flex-col lg:flex-row gap-3">
          {/* Légende compacte */}
          <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2 flex-shrink-0">
            {Object.entries(COULEUR_CFG).map(([c, cfg]) => (
              <div key={c} className="flex items-center gap-1.5">
                <div className={`w-2.5 h-2.5 rounded-full ${cfg.dotCls}`} />
                <span className="text-xs text-slate-600 whitespace-nowrap">{cfg.label}</span>
              </div>
            ))}
          </div>

          {/* Filtres — ligne 1 */}
          <div className="flex flex-1 flex-wrap gap-2">
            <div className="flex-1 min-w-48 relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="text" placeholder="Rechercher par nom ou prénom..." value={search}
                onChange={e => setSearch(e.target.value)} data-testid="search-input"
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white" />
            </div>
            <select value={filterDispositif} onChange={e => setFilterDispositif(e.target.value)}
              data-testid="filter-dispositif"
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 outline-none bg-white">
              <option value="">Tous les dispositifs</option>
              {DISPOSITIFS.filter(d => d).map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={filterCouleur} onChange={e => setFilterCouleur(e.target.value)}
              data-testid="filter-couleur"
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 outline-none bg-white">
              <option value="">Tous les statuts</option>
              {Object.entries(COULEUR_CFG).map(([c, cfg]) => <option key={c} value={c}>{cfg.label}</option>)}
            </select>
            <select value={filterReferent} onChange={e => setFilterReferent(e.target.value)}
              data-testid="filter-referent"
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 outline-none bg-white">
              <option value="">Tous les référents</option>
              {referentList.map(r => <option key={r} value={r}>{r}</option>)}
            </select>

            {/* Filtre date de demande */}
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-3 py-1.5">
              <span className="text-xs font-medium text-slate-500 whitespace-nowrap">Demande :</span>
              <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
                data-testid="filter-date-from"
                className="text-xs border-0 outline-none bg-transparent text-slate-700 cursor-pointer" />
              <span className="text-xs text-slate-400">→</span>
              <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
                data-testid="filter-date-to"
                className="text-xs border-0 outline-none bg-transparent text-slate-700 cursor-pointer" />
              {(filterDateFrom || filterDateTo) && (
                <button onClick={() => { setFilterDateFrom(""); setFilterDateTo(""); }}
                  className="ml-1 text-slate-400 hover:text-red-500 transition-colors text-xs font-bold" title="Effacer">
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bandeau import en cours */}
      {importLoading && (
        <div className="mx-0 mt-3 rounded-xl bg-indigo-600 text-white px-5 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0" />
              <div>
                <p className="font-semibold text-sm">Import en cours dans SharePoint…</p>
                <p className="text-xs text-indigo-200 mt-0.5">
                  {importProgress.status === "parsing" ? "Lecture du fichier Excel…"
                    : importProgress.status === "running" ? `${importProgress.imported} / ${importProgress.total} entrées créées`
                    : "Initialisation…"}
                </p>
              </div>
            </div>
            {importProgress.total > 0 && (
              <span className="text-2xl font-bold tabular-nums">
                {Math.round((importProgress.imported / importProgress.total) * 100)}%
              </span>
            )}
          </div>
          {/* Barre de progression réelle */}
          <div className="w-full h-2 bg-indigo-500 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: importProgress.total > 0 ? `${Math.max(1, Math.round((importProgress.imported / importProgress.total) * 100))}%` : "2%" }}
            />
          </div>
          <p className="text-xs text-indigo-200 mt-2">Ne fermez pas la page.</p>
        </div>
      )}

      {/* Résultat import */}
      {importResult && (
        <div className={`mx-0 mt-3 rounded-xl px-4 py-3 flex items-start gap-3 text-sm border ${
          importResult.type === "success"
            ? "bg-green-50 border-green-200 text-green-800"
            : importResult.type === "warning"
            ? "bg-amber-50 border-amber-200 text-amber-800"
            : "bg-red-50 border-red-200 text-red-800"
        }`}>
          <span className="text-lg flex-shrink-0">
            {importResult.type === "success" ? "✅" : importResult.type === "warning" ? "⚠️" : "❌"}
          </span>
          <div className="flex-1">
            <p className="font-semibold">{importResult.message}</p>
            {importResult.details && (
              <p className="text-xs mt-1 opacity-75 font-mono break-all">{importResult.details}</p>
            )}
          </div>
          <button onClick={() => setImportResult(null)} className="text-lg opacity-50 hover:opacity-100 flex-shrink-0">✕</button>
        </div>
      )}

      {/* Table — conteneur avec scroll interne pour thead sticky */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "calc(100vh - 185px)" }}>
          <table className="w-full text-sm" data-testid="waitlist-table">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-100 border-b-2 border-slate-300">
                {activeCols.map(col => col.sortField
                  ? <SortHeader key={col.key} field={col.sortField} label={col.label} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  : <th key={col.key} className="text-left px-2 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 bg-slate-100">{col.label}</th>
                )}
                <th className="text-left px-2 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 w-16 bg-slate-100">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={activeCols.length + 1} className="text-center py-12">
                  <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </td></tr>
              ) : paginated.length === 0 ? (
                <tr><td colSpan={activeCols.length + 1} className="text-center py-12 text-slate-400 text-sm">Aucune entrée trouvée</td></tr>
              ) : paginated.map((entry, idx) => {
                const cfg = COULEUR_CFG[entry.couleur] || COULEUR_CFG.blanc;
                const isChanging = changingColor === entry.id;
                const cellFor = (key) => {
                  switch(key) {
                    case "priorite": return (
                      <td key={key} className="px-2 py-2 text-slate-400 text-xs font-mono" title="Ordre de priorité">{entry.priorite ?? "—"}</td>
                    );
                    case "couleur": return (
                      <td key={key} className="px-2 py-2 relative">
                        {isChanging && (
                          <div className="absolute z-20 left-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl p-2 flex flex-col gap-1 min-w-[160px]">
                            {Object.entries(COULEUR_CFG).map(([c, cc]) => (
                              <button key={c} onClick={() => handleColorChange(entry.id, c)}
                                className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-slate-50 text-xs font-medium text-slate-700 text-left">
                                <span className={`w-3 h-3 rounded-full ${cc.dotCls}`} />{cc.label}
                              </button>
                            ))}
                            <button onClick={() => setChangingColor(null)} className="text-xs text-slate-400 px-3 py-1 mt-1">Fermer</button>
                          </div>
                        )}
                        <button onClick={() => setChangingColor(isChanging ? null : entry.id)}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 ${cfg.badgeCls}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotCls}`} />{cfg.label}
                        </button>
                      </td>
                    );
                    case "dispositif": return (
                      <td key={key} className="px-2 py-2 text-xs font-medium text-slate-700 max-w-[120px]">
                        <span className="truncate block" title={entry.dispositif}>{entry.dispositif || "—"}</span>
                      </td>
                    );
                    case "nom_prenom": return (
                      <td key={key} className="px-2 py-2">
                        <p className="text-xs font-medium text-slate-900 truncate max-w-[120px]">
                          {[entry.nom, entry.prenom].filter(Boolean).join(" ") || "—"}
                        </p>
                      </td>
                    );
                    case "date_naissance": return (
                      <td key={key} className="px-2 py-2 text-xs text-slate-600 whitespace-nowrap">{entry.date_naissance || "—"}</td>
                    );
                    case "nb_enfants": return (
                      <td key={key} className="px-2 py-2 text-xs text-slate-600 text-center">{entry.nb_enfants ?? "—"}</td>
                    );
                    case "degre_accompagnement": return (
                      <td key={key} className="px-2 py-2 text-xs text-center">
                        {entry.degre_accompagnement
                          ? <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                              entry.degre_accompagnement === 1 ? "bg-green-100 text-green-700" :
                              entry.degre_accompagnement === 2 ? "bg-amber-100 text-amber-700" :
                              "bg-red-100 text-red-700"
                            }`}>{entry.degre_accompagnement}</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                    );
                    case "sante": return (
                      <td key={key} className="px-2 py-2 text-xs text-slate-600 max-w-[100px]">
                        <span className="truncate block" title={entry.sante}>{entry.sante || "—"}</span>
                      </td>
                    );
                    case "composition": return (
                      <td key={key} className="px-2 py-2 text-xs text-slate-600 max-w-[100px]">
                        <span className="truncate block" title={entry.composition_familiale}>{entry.composition_familiale || "—"}</span>
                      </td>
                    );
                    case "date_demande": return <td key={key} className="px-2 py-2 text-xs text-slate-600 whitespace-nowrap">{entry.date_demande || "—"}</td>;
                    case "date_commission": return <td key={key} className="px-2 py-2 text-xs text-slate-600 whitespace-nowrap">{entry.date_commission || "—"}</td>;
                    case "secteur": return (
                      <td key={key} className="px-2 py-2 text-xs text-slate-600 max-w-[80px]">
                        <span className="truncate block" title={entry.secteur_geographique}>{entry.secteur_geographique || "—"}</span>
                      </td>
                    );
                    case "referent": return <td key={key} className="px-2 py-2"><ReferentBadge name={entry.referent} colorMap={referentColorMap} /></td>;
                    case "ressource": return (
                      <td key={key} className="px-2 py-2 text-xs text-slate-600 max-w-[80px]">
                        <span className="truncate block" title={entry.ressource}>{entry.ressource || "—"}</span>
                      </td>
                    );
                    case "commentaire": return (
                      <td key={key} className="px-2 py-2 max-w-[160px]">
                        {entry.commentaire
                          ? <span className="text-xs text-slate-600 line-clamp-2" title={entry.commentaire}>
                              {entry.commentaire.length > 60 ? entry.commentaire.slice(0, 60) + "…" : entry.commentaire}
                            </span>
                          : <span className="text-slate-300 text-xs">—</span>}
                      </td>
                    );
                    case "date_commentaire": return (
                      <td key={key} className="px-2 py-2 text-xs whitespace-nowrap">
                        {entry.date_commentaire
                          ? <span className="font-medium text-indigo-700">{entry.date_commentaire}</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                    );
                    default: return <td key={key} className="px-2 py-2 text-xs text-slate-400">—</td>;
                  }
                };
                return [
                  <tr key={entry.id} className={`${cfg.rowCls} transition-colors`} data-testid={`waitlist-row-${entry.id}`}>
                    {activeCols.map(col => cellFor(col.key))}
                    <td className="px-2 py-2">
                      <div className="grid grid-cols-3 gap-0 w-20">
                        {entry.commentaire ? (
                          <button onClick={() => setExpandedComment(expandedComment === entry.id ? null : entry.id)}
                            className="p-1 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors">
                            <MessageSquare className="w-3 h-3" />
                          </button>
                        ) : <span className="w-5 h-5 block" />}
                        <button onClick={() => { setEditEntry(entry); setModalOpen(true); }}
                          data-testid={`edit-btn-${entry.id}`}
                          className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors">
                          <Edit2 className="w-3 h-3" />
                        </button>
                        {isAdmin
                          ? <button onClick={() => setDeleteId(entry.id)} data-testid={`delete-btn-${entry.id}`}
                              className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          : <span className="w-5 h-5 block" />}
                      </div>
                    </td>
                  </tr>,
                  expandedComment === entry.id && entry.commentaire && (
                    <tr key={`comment-${entry.id}`} className={cfg.rowCls}>
                      <td colSpan={activeCols.length + 1} className="px-3 pb-2">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 leading-relaxed max-h-32 overflow-y-auto">
                          <span className="font-semibold">Commentaire : </span>{entry.commentaire}
                        </div>
                      </td>
                    </tr>
                  )
                ].filter(Boolean);
              }).flat()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <WaitlistEntryModal entry={editEntry} onSave={handleSave} onClose={() => { setModalOpen(false); setEditEntry(null); }} />
      )}

      {/* Confirm reset */}
      {resetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={() => !resetLoading && setResetConfirm(false)} />
          <div className="relative bg-white rounded-xl shadow-xl p-6 max-w-sm w-full">
            {resetLoading ? (
              <div className="text-center py-4">
                <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="font-semibold text-slate-900">Vidage en cours...</p>
                <p className="text-sm text-slate-500 mt-1">La liste SharePoint est en cours de réinitialisation</p>
              </div>
            ) : (
              <>
                <h3 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Vider toute la liste ?
                </h3>
                <p className="text-sm text-slate-500 mb-2">
                  Cette action supprime <strong>toutes les entrées</strong> de façon irréversible.
                </p>
                <p className="text-sm text-indigo-600 bg-indigo-50 rounded-lg px-3 py-2 mb-5">
                  Conseil : exportez d'abord la liste avant de la vider.
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setResetConfirm(false)}
                    className="flex-1 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50">
                    Annuler
                  </button>
                  <button onClick={handleReset}
                    className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700">
                    Vider la liste
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={() => setDeleteId(null)} />
          <div className="relative bg-white rounded-xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Confirmer la suppression</h3>
            <p className="text-sm text-slate-500 mb-5">Cette action est irréversible.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} data-testid="cancel-delete-btn"
                className="flex-1 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50">
                Annuler
              </button>
              <button onClick={() => handleDelete(deleteId)} data-testid="confirm-delete-btn"
                className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700">
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
